<script lang="ts">
  /**
   * One theme in a week's digest: what they asked, who asked it, whether the course
   * answers it, and what to make.
   *
   * A ROW, NOT A CARD. The first version gave each theme three full-width bands —
   * header, action, an expander — stacked behind hairlines, which read as three things
   * per theme and put nine of them over three thousand pixels. An owner scanning a
   * Monday list is comparing themes to EACH OTHER, so what matters is that row N and
   * row N+1 line up: same columns, same order, one glance each. Everything is three
   * lines in one box now.
   *
   * SCAN ORDER LEFT TO RIGHT, then down. The RAIL says whether the course covers it
   * (the app's lit-vs-dimmed signature, `error`/`warning`/`success` — no new hue), the
   * FACES say how many real people are stuck, the SPARKLINE says building or fading.
   * Only the action sentence is prose, because it is the one part meant to be read.
   *
   * THE SPARKLINE NEEDS AN EXPLICIT HEIGHT, and that is not incidental. `<Sparkline>`
   * takes its size from the caller's classes and fills whatever box it is given; handed
   * a width alone inside a flex row it stretched to the row's full height and rendered
   * as a seismograph. A trend here is a glyph, not a chart — `h-7` is what says so.
   *
   * WHY FACES AND NOT A NUMBER. "8 members" and eight faces are the same datum, but the
   * faces are the part an owner believes — these are their people, and one of them is
   * usually somebody they recognise. `<AvatarStack>` deduplicates and takes `total`, so
   * three avatars beside "nine asked" is not a quiet lie.
   *
   * THE EVIDENCE IS A THREAD. A question is half a conversation — whether the
   * community answered it themselves is exactly what tells an owner if it is really a
   * gap — so expanding shows `<PostThread>`, which is what `/admin/moderation` and the
   * timeline's drawer show, and what the Helper renders too. Redaction is inherited:
   * everything inside it carries `.redact` itself.
   */
  import AvatarStack from '@ui/AvatarStack.svelte';
  import AskCard from './AskCard.svelte';
  import Sparkline from '../charts/Sparkline.svelte';
  import type { DigestQuote, DigestTheme } from '@api';

  let {
    theme,
    open = false,
    ontoggle = null,
    onopenthread = null,
  }: {
    theme: DigestTheme;
    open?: boolean;
    ontoggle?: (() => void) | null;
    /** A question was clicked — the page opens the thread on it. Owned there rather
     *  than here so one drawer serves every card on the page. */
    onopenthread?: ((q: DigestQuote) => void) | null;
  } = $props();

  /** The rail is a PAINTED BAR, not a left border. `.hair` sets `border-color` on all
   *  four sides and wins the cascade against a `border-l-*` utility, so the coloured
   *  border silently rendered grey — the single most important signal on the row,
   *  invisible. An absolutely-positioned span cannot be overridden by a border rule. */
  const RAIL: Record<string, string> = {
    missing: 'bg-error',
    thin: 'bg-warning',
    covered: 'bg-success',
  };
  /** A tinted pill, not coloured text. Coverage is the one value on the meta line that
   *  must read at a glance across nine rows, and `.eyebrow` sets its own colour — so
   *  tinting the background is both louder and immune to that collision. */
  const COVERAGE_PILL: Record<string, string> = {
    missing: 'bg-error/10 text-error',
    thin: 'bg-warning/15 text-warning',
    covered: 'bg-success/10 text-success',
  };
  const COVERAGE_WORD: Record<string, string> = {
    missing: 'No lesson',
    thin: 'Thin',
    covered: 'Covered',
  };
  const ACTION_WORD: Record<string, string> = {
    create_lesson: 'Make a lesson',
    update_lesson: 'Update a lesson',
    make_content: 'Post about it',
    none: '',
  };

  const cov = $derived(theme.coverage ?? '');
  const people = $derived(theme.quotes.map((q) => q.author).filter((a) => a != null));
  const spark = $derived(theme.trend.map((p) => p.count));
  const sparkLabels = $derived(theme.trend.map((p) => p.week.slice(5)));
  const actionWord = $derived(ACTION_WORD[theme.action_kind ?? 'none']);


</script>

<article class="rounded-box relative overflow-hidden border hair bg-base-100">
  <span
    class="absolute inset-y-0 left-0 w-[3px] {RAIL[cov] ?? 'bg-base-300'}"
    aria-hidden="true"
  ></span>
  <!-- The whole row toggles. There is one thing to do with a theme — read the evidence
       behind it — so the affordance is the row, not a link buried under it. -->
  <button
    class="flex w-full items-center gap-4 py-2.5 pr-4 pl-5 text-left hover:bg-base-200/40"
    onclick={() => ontoggle?.()}
    aria-expanded={open}
  >
    <div class="min-w-0 flex-1">
      <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <h3 class="redact font-display text-[0.94rem] leading-tight font-semibold">{theme.title}</h3>
        {#if theme.is_new}
          <span class="text-[0.68rem] text-base-content/45">new</span>
        {:else if theme.streak_weeks > 1}
          <!-- The most valuable sentence this product can say. Amber past three weeks:
               that is where "recurring" stops being noise and becomes a backlog item. -->
          <span
            class="text-[0.68rem] {theme.streak_weeks >= 3
              ? 'font-medium text-primary'
              : 'text-base-content/45'}">{theme.streak_weeks} wks running</span
          >
        {/if}
      </div>

      <!-- TWO LINES, AND THE SECOND IS FACTS ONLY.
           It was four: title, meta, and forty words of action prose clamped to two
           lines that ended mid-sentence on nine cards out of ten. Ten of those is a
           wall, and the clamped half-sentence was unreadable anyway — you had to expand
           to finish it, which is exactly what expanding is for.
           What survives is what you triage on: how much demand, whether the course
           answers it, and what to do. The ACTION KIND stays because it is the verdict
           in three words; the action TEXT goes below the fold with the lesson it names
           and the questions behind it. -->
      <div class="mt-1 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
        <AvatarStack {people} total={theme.member_count} size={20} max={3} />
        <span class="shrink-0 text-[0.72rem] tabular-nums text-base-content/50">
          {theme.question_count} question{theme.question_count === 1 ? '' : 's'} · {theme.member_count}
          {theme.member_count === 1 ? 'person' : 'people'}
        </span>
        {#if cov}
          <span
            class="shrink-0 rounded-full px-1.5 py-px font-mono text-[0.58rem] tracking-wide uppercase
                   {COVERAGE_PILL[cov]}">{COVERAGE_WORD[cov]}</span
          >
        {/if}
        {#if actionWord}
          <!-- SPELLED OUT, NOT `.eyebrow` — the same collision the coverage pill above
               dodges. `.eyebrow` is declared OUTSIDE any `@layer` in app.css, and
               unlayered CSS beats Tailwind's layered utilities, so its own colour wins
               and `text-primary` beside it never applies. The action is the one thing
               on this row meant to be coloured, so it carries the style directly. -->
          <span class="shrink-0 text-[0.6rem] text-primary font-mono tracking-[0.13em] uppercase">{actionWord}</span>
        {/if}
      </div>
    </div>

    {#if spark.length > 1 && spark.some((v) => v > 0)}
      <!-- Zero-filled server-side across THE WEEKS THIS COMMUNITY HAS A DIGEST FOR (up
           to twelve). A series built only from the weeks a topic appeared in is flat
           whatever actually happened — but filling weeks that were never digested is
           worse, because those zeros are invented. On a young community that drew nine
           fake zeros and a spike on every card, so every trend looked the same.

           TWO POINTS MINIMUM. One point is not a trend, it is a dot, and a lone dot in
           a chart slot reads as a rendering fault. -->
      <div class="hidden shrink-0 sm:block">
        <Sparkline
          values={spark}
          labels={sparkLabels}
          format={(v) => `${v} asked`}
          class="h-7 w-20 text-base-content/40"
        />
      </div>
    {/if}

    <svg
      class="h-3.5 w-3.5 shrink-0 text-base-content/30 transition-transform {open ? 'rotate-180' : ''}"
      viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"
    ><path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" /></svg>
  </button>

  {#if open}
    <div class="border-t hair bg-base-200/30 p-4">
      {#if theme.action_text}
        <!-- The action in full, and the lesson it was judged against directly under it.
             They are one statement — "thin, and the nearest thing you have is this" —
             and the lesson title is long enough (a real one: "7/22/26 GHL Workflows,
             Sales Framework & GBP Fixes") that it needs a line of its own rather than a
             slot on a meta row it used to get truncated in. -->
        <div class="rounded-box mb-3 border hair bg-base-100 px-3 py-2.5">
          {#if actionWord}
            <div class="mb-1 text-[0.58rem] text-primary font-mono tracking-[0.13em] uppercase">{actionWord}</div>
          {/if}
          <p class="redact text-[0.84rem] leading-relaxed">{theme.action_text}</p>
          {#if theme.best_page_title}
            <div class="mt-2 flex items-center gap-1.5 border-t hair pt-2 text-base-content/45">
              <svg class="h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" aria-hidden="true"
              ><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"
                     stroke-linecap="round" stroke-linejoin="round" /></svg>
              <span class="redact min-w-0 truncate text-[0.72rem]">{theme.best_page_title}</span>
            </div>
          {/if}
        </div>
      {/if}
      {#if theme.summary}
        <p class="redact mb-3 text-[0.84rem] leading-relaxed text-base-content/65">{theme.summary}</p>
      {/if}
      <div class="space-y-2">
        <!-- A FLAT LIST OF EXCERPTS. No grouping by thread any more: grouping existed
             to stop the same conversation being drawn twice, and nothing here draws a
             conversation. Each row is one question; the thread is a click away. -->
        {#each theme.quotes as q (q.question_id)}
          <AskCard quote={q} onopen={() => onopenthread?.(q)} />
        {/each}

      </div>
    </div>
  {/if}
</article>
